import Component from '@ux/component';
import PopOver from '@ux/popover';
import Link from '@ux/link';
import { IntlProvider, FormattedMessage } from 'react-intl';
import React from 'react';
import PropTypes from 'prop-types';
import Button from '@ux/button';
import memoize from 'memoize-one';
import golfToEid from 'golf-to-eid';
import ChevDown from '@ux/icon/chevron-down-lt';

const isDataAttr = /^data-[a-zA-Z0-9-]+$/;

/**
 * Mapping of the margins used on nav items in various breakpoints
 */
const navItemMargins = {
  tablet: 16,
  desktop: 24,
  large: 32
};

/**
 * @typedef {object} Item
 * @prop {string|React.ReactElement} [title] The label of the item (may also be specified as caption)
 * @prop {string} [caption] The label of the item (may also be specified as title)
 * @prop {string} [href] The URL to link the item to
 * @prop {React.MouseEventHandler} [onClick] A click event handler to run when the item is clicked
 * @prop {string} [className] A CSS class name to apply to the item
 * @prop {string} [target] The HTML target value to apply to the item
 * @prop {string} [id] A unique identifier for the item
 * @prop {string} [eid] The analytics EID to apply to the item
 * @prop {string} [icon] An icon name to use alongside the item label
 * @prop {boolean} [active] Whether the item is currently active
 * @prop {object} [dataAttrs] A set of data-* attributes to apply to the item
 * @prop {boolean} [mobileOnly] Whether the item should only show in a mobile view (and thus excluded from this nav)
 * @prop {Item[]} [children] An array of children Items
 */

/**
 * @typedef {object} ItemWithDesign
 * @prop {string} [design] The button design to use on the right-side button item
 */

/**
 * @typedef {Item & ItemWithDesign} RightItem
 */

/**
 * Generic header navigation component
 *
 * @public
 */
class HeaderNavigation extends Component {
  /**
   * Constructor for HeaderNavigation component
   *
   * @param {object} props Props for the component
   * @constructor
   * @public
   */
  constructor(props) {
    super(...arguments);

    this.setDataAttrs = this.setDataAttrs.bind(this);
    this.closeNavTrays = this.closeNavTrays.bind(this);
    this.onItemClick = this.onItemClick.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.trayMenus = Object.create(null);

    // For scaling logic
    this.itemRefs = Object.create(null);
    this.containerRef = React.createRef();
    this.lastMoreIndexBuilt = null;
    this.moreMenuChildren = null;
    this.measurements = Object.create(null);
    this.resizeTimeoutId = null;
    this.state = {
      visible: !props.scale,
      firstMoreItemIndex: null
    };

    this.getItems = memoize(items => items.map(this.setDataAttrs));
  }

  /**
   * React lifecycle method called after initial mount of the component
   *
   * @private
   */
  componentDidMount() {
    const { scale, items } = this.props;
    if (scale) {
      if (items.length) {
        this.measure();
        this.scale(true);
      }
      this.attachResizeHandler();
      this.breakpoint('tablet', 'desktop', 'large');
    }
  }

  /**
   * React lifecycle method called after any updates to component props or state
   *
   * @param {object} prevProps Previous props prior to the update
   * @param {object} prevState Previous state prior to the update
   * @private
   */
  componentDidUpdate(prevProps, prevState) {
    const { scale, items } = this.props;
    if (scale && !prevProps.scale) this.attachResizeHandler();
    if (!scale && prevProps.scale) this.detachResizeHandler();

    if (
      scale &&
      items.length &&
      this.didScaleRelevantDataChange(prevProps, prevState)
    ) {
      this.lastMoreIndexBuilt = null;
      this.moreMenuChildren = null;
      this.scale();
    }
  }

  /**
   * React lifecycle method called before unmount of the component
   *
   * @private
   */
  componentWillUnmount() {
    this.detachResizeHandler();
    this.breakup();
  }

  /**
   * Attach the window resize handler if we're in a browser
   *
   * @private
   */
  attachResizeHandler() {
    if (!this.isBrowser) return;
    this.on('resize', this.handleResize);
  }

  /**
   * Detach the window resize handler if we're in a browser
   *
   * @private
   */
  detachResizeHandler() {
    if (!this.isBrowser) return;
    this.off('resize', this.handleResize);
  }

  /**
   * Handle a resize event
   *
   * This method includes debouncing logic to ensure that we only react to resize events after a user has paused
   * dragging for at least 100ms
   * @private
   */
  handleResize() {
    // Resize events will be continually sent as a user drags their browser edge, so we need to debounce this a bit.
    if (this.resizeTimeoutId) {
      clearTimeout(this.resizeTimeoutId);
    } else {
      // Initial resize event -- we should close menus & set resizing class
      this.closeNavTrays();
      this.setState({
        resizing: true
      });
    }

    const self = this;
    this.resizeTimeoutId = setTimeout(() => {
      self.resizeTimeoutId = null;
      self.setState({
        resizing: false
      });
      if (
        self.containerRef.current &&
        self.containerRef.current.offsetWidth !== self.measurements.container
      ) {
        // Container width changed -- must scale
        self.measureContainer();
        self.scale();
      }
    }, 100);
  }

  /**
   * Determine whether a component update changed any props or state that are relevant to scaling logic. That is,
   * changes that would necessitate re-measuring and scaling items
   *
   * @param {object} prevProps Previous props prior to the update
   * @param {object} prevState Previous state prior to the update
   * @returns {boolean} true if a change occurred that necessitates re-measuring/scaling, false otherwise
   * @private
   */
  didScaleRelevantDataChange(prevProps, prevState) {
    const newProps = this.props;
    const newState = this.state;
    const { items: prevItems } = prevProps;
    const { items: newItems } = newProps;
    const { breakpoint: prevBreakpoint } = prevState;
    const { breakpoint: newBreakpoint } = newState;
    return (
      prevItems.length !== newItems.length ||
      prevBreakpoint !== newBreakpoint ||
      newItems.some((newItem, i) => {
        const prevItem = prevItems[i];
        return (
          // icon changed
          // caption changed
          // title changed
          prevItem.title !== newItem.title ||
          prevItem.caption !== newItem.caption ||
          prevItem.icon !== newItem.icon || // whether the item has children or not changed
          (prevItem.children && !!prevItem.children.length) !==
            (newItem.children && !!newItem.children.length)
        );
      })
    );
  }

  /**
   * Measure the main container element (UL) and all item elements (LIs) and store their sizes
   *
   * @private
   */
  measure() {
    this.measureContainer();
    for (const itemRefKey in this.itemRefs) {
      // eslint-disable-line guard-for-in
      if (this.itemRefs[itemRefKey]) {
        this.measurements[itemRefKey] = this.itemRefs[itemRefKey].offsetWidth;
      }
    }
  }

  /**
   * Measure the main container element (UL)
   *
   * @public
   */
  measureContainer() {
    if (!this.containerRef || !this.containerRef.current) return;
    this.measurements.container = this.containerRef.current.offsetWidth;
  }

  /**
   * Calculate whether all items will fit in the available container width and scale any overflowing items into a More
   * menu as needed.
   *
   * @param {boolean} [shouldMakeVisible=false] Whether to set the `visible` state flag after scaling, defaults to
   *  false
   * @private
   */
  // eslint-disable-next-line max-statements
  scale(shouldMakeVisible) {
    // Get the nav item margin being used
    const navItemMargin =
      navItemMargins[this.state.breakpoint] || navItemMargins.large;

    const allItemsWidth = Object.keys(this.measurements).reduce((acc, key) => {
      if (key !== 'more' && key !== 'container')
        acc += this.measurements[key] + navItemMargin;
      return acc;
    }, 0);

    // Default to showing all items
    let firstMoreItemIndex = null;

    const {
      container: containerWidth,
      more: moreMenuWidth
    } = this.measurements;

    // If not all items will fit, find how many will
    if (allItemsWidth > containerWidth) {
      // Start with full width available
      let spaceRemaining = containerWidth;

      // Since we know we'll scale at least 1 item to More menu, we must subtract the more menu width
      spaceRemaining -= moreMenuWidth + navItemMargin;

      firstMoreItemIndex = 0;
      while (firstMoreItemIndex < this.props.items.length) {
        if (
          spaceRemaining -
            (this.measurements[firstMoreItemIndex] + navItemMargin) >
          0
        ) {
          // Item fits
          spaceRemaining -=
            this.measurements[firstMoreItemIndex] + navItemMargin;
          firstMoreItemIndex++;
        } else {
          break;
        }
      }
    }

    const newState = {
      firstMoreItemIndex
    };
    if (shouldMakeVisible) newState.visible = true;
    this.setState(newState);
  }

  /**
   * Close all navigation menus
   *
   * @private
   */
  closeNavTrays() {
    for (const menu in this.trayMenus) {
      // eslint-disable-line guard-for-in
      if (this.trayMenus[menu]) {
        this.trayMenus[menu].close();
      }
    }
  }

  /**
   * Function to call when clicking an item.
   *
   * @param {Item} item The item containing a custom onClick function
   * @param {React.MouseEvent} event The click event
   * @private
   */
  onItemClick(item, event) {
    if (item.onClick) {
      item.onClick(event);
    }
    this.closeNavTrays();
  }

  /**
   * Sets a dataAttrs field containing all the `data-` attributes on a given item.
   *
   * @param {Item} item A navigation item
   * @returns {Item} The updated navigation item with a `dataAttrs` field
   * @private
   */
  setDataAttrs(item) {
    if (!item) return;

    item.dataAttrs = Object.keys(item).reduce(function (acc, k) {
      if (isDataAttr.test(k)) {
        acc[k] = item[k];
      }
      return acc;
    }, {});

    if (item.children) {
      item.children.forEach(this.setDataAttrs);
    }

    return item;
  }

  /**
   * Determine whether an item has an existing eid, or if one should be generated.
   *
   * @param {object} item object from the passed via the  props.items array
   * @param {string} postfix postfix to be applied if an eid must be generated
   * @returns {string} the found or generated eid
   * @private
   */
  generateEid(item = {}, postfix = '') {
    if (item.eid) {
      return item.eid;
    }

    if (!item.id) {
      return;
    }

    const prefix = this.props.eidPrefix;
    return golfToEid(item.id, {
      prefix,
      postfix
    });
  }

  /**
   * Render a uxicon.
   *
   * @param {string} classname The classname corresponding to the uxicon
   * @returns {React.ReactElement} Span containing a uxicon
   * @private
   */
  renderNavIcon(classname) {
    return <span className={ this.classNames(classname, 'icon', 'uxicon') } />;
  }

  /**
   * Render a column of nav items. Used inside of a multi-column tray.
   * @param  {Item} item Navigation item whose children to render in a column
   * @param  {string} key The key for the heading list elements
   * @returns {React.ReactElement[]} Array of list elements containing a heading and links
   */
  renderColumn(item, key) {
    if (item.mobileOnly) {
      return null;
    }
    const columnItems = [];

    // push the heading for the column if there is one
    if (item.caption || item.title) {
      columnItems.push(
        <li key={ key } className={ 'heading title small' } { ...item.dataAttrs }>
          { item.caption || item.title }
          { item.icon && this.renderNavIcon(item.icon) }
        </li>
      );
    }
    // then push the links
    for (let i = 0; i < item.children.length; i++) {
      const childItem = item.children[i];
      if (childItem.mobileOnly) {
        // eslint-disable-next-line no-continue
        continue;
      }
      columnItems.push(
        this.renderSingleLink(
          childItem,
          (childItem.caption || childItem.id || key) + '_' + i
        )
      );
    }
    return columnItems;
  }

  /**
   * Render a single nav item. Used inside of a single column tray.
   * @param  {Item} item Navigation item to render
   * @param  {string} key The key for each list element
   * @returns {React.ReactElement} List element containing a @ux/link
   */
  renderSingleLink(item, key) {
    const classNames = this.classNames(
      item.className,
      { active: item.active }
    );
    const props = {
      'className': classNames,
      'target': item.target,
      'id': item.id,
      'href': item.href || '#',
      'data-eid': this.generateEid(item, 'link.click'),

      'onClicked': e => {
        this.onItemClick(item, e);
      },

      ...item.dataAttrs
    };
    return (
      <li key={ key } className={ props.className }>
        <Link { ...props } urlArgs={ this.props.urlArgs }>
          <span>
            { item.caption || item.title }
            { item.icon && this.renderNavIcon(item.icon) }
          </span>
        </Link>
      </li>
    );
  }

  /**
   * Create a list with all nested data structures
   * rendered appropriately.
   *
   * @param {object} navItem the parent nav item of a collection of columns
   * @param {boolean} [multi] True if the nav structure is multi-column
   * @returns {React.ReactElement} Columns
   * @private
   */
  renderColumns(navItem, multi) {
    if (multi) {
      return navItem.children.map((child, k) => {
        return (
          <ul
            key={ k }
            className='list-unstyled nav-column'
            { ...child.dataAttrs }>
            { this.renderColumn(child, k) }
          </ul>
        );
      });
    }

    return (
      <ul className='list-unstyled nav-column' { ...navItem.dataAttrs }>
        {navItem.children.map((child, k) => {
          if (child.mobileOnly) {
            return null;
          }
          return this.renderSingleLink(child, k);
        })}
      </ul>
    );
  }

  /**
   * Determines if the root navigation's structure is multi- or single columned
   *
   * @param {Item} navItem the root node of the nav structure
   * @returns {boolean} true if multi column
   * @private
   */
  isMultiColumn(navItem) {
    return (
      navItem.children &&
      navItem.children.every(child => Array.isArray(child.children))
    );
  }

  /**
   * Render items that appear on the right side of the navigation
   * @param  {RightItem} item Object containing details of the navigation item
   * @param  {React.ReactElement} icon Span containing a uxicon
   * @param  {number|string} i Index value
   * @returns {React.ReactElement} Either a button-link or a string of text
   * @private
   */
  renderNavRightItem(item, icon, i) {
    if (item.href) {
      const design = item.design || 'default';

      return (
        <li key={ i } className='nav-item right-button'>
          <Button
            design={ design }
            size='small'
            id={ item.id }
            href={ item.href }
            target={ item.target }
            onClick={ item.onClick }
            data-eid={ item.eid }
            { ...item.dataAttrs }>
            { icon }
            { item.caption }
          </Button>
        </li>
      );
    }
    return (
      <li key={ i } className='nav-item right-text'>
        { item.caption }
      </li>
    );
  }

  /**
   * Render some of the navigation items
   *
   * @param {Item[]} items Navigation items
   * @param {number} [howMany] How many items to render
   * @returns {React.ReactElement[]} Rendered items
   */
  renderSomeItems(items, howMany) {
    const result = [];
    if (!howMany && howMany !== 0) howMany = items.length;
    for (let i = 0; i < howMany; i++) {
      result.push(this.renderItem(items[i], i));
    }
    return result;
  }

  /**
   * Render a navigation item
   *
   * @param {Item} item nav item
   * @param {number|string} i index value
   * @returns {React.ReactElement} ReactElement
   * @private
   */
  renderItem(item, i) {
    const props = this.props;
    if (item.mobileOnly) {
      return null;
    }
    const classNames = this.classNames(
      'nav-item',
      { active: item.active },
      item.className
    );

    let icon;
    if (item.icon) {
      icon = this.renderNavIcon(item.icon);
    }

    //
    // Render a tray-menu if the current item has children regardless of side
    //
    if (item.children && item.children.length) {
      const multiColumn = this.isMultiColumn(item);
      const columns = this.renderColumns(item, multiColumn);
      const trayTitle = (
        <React.Fragment>
          { item.caption || item.title }
          { icon }
          <ChevDown />
        </React.Fragment>
      );

      return (
        <li
          className={ classNames }
          key={ i }
          ref={ r => {
            this.itemRefs[i] = r;
          } }>
          <PopOver
            ref={ r => {
              this.trayMenus[i] = r;
            } }
            className={
              this.classNames({
                'multi-col': multiColumn
              })
            }
            control={ trayTitle }
            eid={ this.generateEid(item, 'popover.click') }
            openOnHover={ true }
            { ...item.dataAttrs }>
            { props.showTitleInsideTray && trayTitle }
            <div className='flex-columns'>{ columns }</div>
          </PopOver>
        </li>
      );
    }

    // if no children, handle rendering for right side
    if (props.side === 'right') {
      return this.renderNavRightItem(item, icon, i);
    }

    // handle render for left side
    return (
      <li
        key={ i }
        className={ classNames }
        ref={ r => {
          this.itemRefs[i] = r;
        } }>
        <Link
          className='nav-link font-base single-parent-link'
          id={ item.id }
          href={ item.href || '#' }
          target={ item.target }
          onClicked={ item.onClick }
          data-eid={ this.generateEid(item, 'link.click') }
          urlArgs={ props.urlArgs }
          { ...item.dataAttrs }>
          { item.caption || item.title }
          { icon }
        </Link>
      </li>
    );
  }

  /**
   * Rener the Mre menu in a scaling nav
   *
   * @param {Item[]} allItems The full collection of items to be displayed (not just in the More menu)
   * @returns {React.ReactElement} The More menu React element
   * @private
   */
  renderMoreMenu(allItems) {
    const { firstMoreItemIndex, visible } = this.state;

    if (
      visible &&
      (firstMoreItemIndex === null || firstMoreItemIndex >= allItems.length)
    )
      return null;

    const moreItem = this.getMoreMenuItem(allItems);
    return this.renderItem(moreItem, 'more');
  }

  /**
   * Get the Item object to describe the More menu
   *
   * @param {Item[]} allItems The full collection of items to be displayed (not just in the More menu)
   * @returns {Item} An Item object representing the More menu
   * @private
   */
  getMoreMenuItem(allItems) {
    const { moreMenuEid } = this.props;
    return {
      children: this.getMoreItemChildren(allItems),
      title: <FormattedMessage id='Shared:HeaderNavigation:More' />,
      eid: moreMenuEid,
      className: 'more-menu'
    };
  }

  /**
   * Get the children that should appear in the More menu - from pre-cached copy if available
   *
   * @param {Item[]} allItems All of the menu items
   * @returns {Item[]} An array of nav items for the items that should appear in the More menu
   * @private
   */
  getMoreItemChildren(allItems) {
    const { firstMoreItemIndex, visible } = this.state;

    if (!visible) {
      // The menu is not visible -- this is just for scaling before initial render. So we can just toss in a dummy link
      return [
        {
          title: 'Dummy link for scaling',
          href: '#'
        }
      ];
    }

    if (
      this.lastMoreIndexBuilt === firstMoreItemIndex &&
      this.moreMenuChildren
    ) {
      return this.moreMenuChildren;
    }

    this.lastMoreIndexBuilt = firstMoreItemIndex;

    if (firstMoreItemIndex === null || firstMoreItemIndex >= allItems.length) {
      this.moreMenuChildren = [];
    } else {
      this.moreMenuChildren = this.buildMoreItemChildren(
        allItems,
        firstMoreItemIndex
      );
    }

    return this.moreMenuChildren;
  }

  /**
   * Build the More menu children array
   *
   * @param {Item[]} allItems All of the menu items
   * @param {number} firstMoreItemIndex The index of the first item to show in the More menu
   * @returns {Item[]} An array of nav items for the items that should appear in the More menu
   * @private
   */
  buildMoreItemChildren(allItems, firstMoreItemIndex) {
    const allMoreChildren = allItems.slice(firstMoreItemIndex);
    const moreChildrenLinks = [];
    const moreChildrenMenus = [];
    for (const moreChild of allMoreChildren) {
      if (moreChild.children && moreChild.children.length) {
        moreChildrenMenus.push(moreChild);
      } else {
        moreChildrenLinks.push(moreChild);
      }
    }

    if (moreChildrenMenus.length) {
      if (moreChildrenLinks.length) {
        return [
          {
            title: <FormattedMessage id='Shared:HeaderNavigation:More' />,
            children: moreChildrenLinks
          },
          ...moreChildrenMenus
        ];
      }

      return moreChildrenMenus;
    }

    return moreChildrenLinks;
  }

  /**
   * Render the navigation component
   *
   * @returns {React.ReactElement} ReactElement
   */
  render() {
    const props = this.props;
    const allItems = this.getItems(props.items);

    if (!allItems.length) {
      return null;
    }

    const { firstMoreItemIndex, visible, resizing } = this.state;
    const visibleItems = this.renderSomeItems(allItems, firstMoreItemIndex);

    // Note: we need to ensure that the nav is not display:none during the initial hidden scaling measurements. So we
    // only set the d-none class if we're rendering visible.

    return (
      <IntlProvider locale={ props.market } messages={ props.messages }>
        <div
          className={ this.classNames(
            'flex-row',
            'd-lg-flex',
            visible && 'd-none',
            'header-nav-wrap'
          ) }>
          <nav className='header-nav'>
            <ul
              className={ this.classNames(
                'nav',
                `nav-${props.side}`,
                !visible && 'invisible-header-nav',
                resizing && 'resizing'
              ) }
              ref={ this.containerRef }>
              { visibleItems }
              { props.scale && this.renderMoreMenu(allItems) }
            </ul>
          </nav>
        </div>
      </IntlProvider>
    );
  }
}

HeaderNavigation.defaultProps = {
  side: 'left',
  eidPrefix: 'uxp.hyd',
  items: [],
  showTitleInsideTray: false,
  scale: false
};

HeaderNavigation.propTypes = {
  urlArgs: PropTypes.object,
  showTitleInsideTray: PropTypes.bool,
  side: PropTypes.oneOf(['left', 'right']),
  scale: validateScalePropType,
  moreMenuEid: PropTypes.string,
  market: PropTypes.string.isRequired,
  messages: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired,
  eidPrefix: PropTypes.string
};

function validateScalePropType(props, propName, componentName) {
  if (typeof props[propName] !== 'boolean') {
    return new Error(
      'Invalid prop `' +
        propName +
        '` supplied to' +
        ' `' +
        componentName +
        '`. Expected boolean value. Validation failed.'
    );
  }
  if (props[propName] && props.side === 'right') {
    return new Error(
      'Invalid prop `' +
        propName +
        '` supplied to' +
        ' `' +
        componentName +
        '`. Scaling is not supported on right-side menus. Validation failed.'
    );
  }
}

export default HeaderNavigation;
